import { useState, useEffect, useRef } from 'react';

const LOADING_DURATION = 7000;
const SETTLE_DURATION = 800;
const LOADING_CEILING = 0.8;
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

export function useCountUpAnimation(
    approximation: number | undefined,
    finalValue: number | undefined,
    isLoading: boolean
): number | undefined {
    const [display, setDisplay] = useState<number | undefined>();
    const currentReference = useRef<number | undefined>();
    const frameReference = useRef<number | undefined>();
    const phaseReference = useRef<'loading' | 'settling'>('loading');
    const startTimeReference = useRef<number | undefined>();
    const settleFromReference = useRef<number | undefined>();
    const approximationReference = useRef<number | undefined>();
    const finalValueReference = useRef<number | undefined>();

    approximationReference.current = approximation;
    finalValueReference.current = finalValue;

    useEffect(() => {
        if (approximationReference.current === undefined) return;

        const tick = (ts: number) => {
            if (startTimeReference.current === undefined)
                startTimeReference.current = ts;
            const elapsed = ts - startTimeReference.current;

            if (phaseReference.current === 'loading') {
                const t = Math.min(elapsed / LOADING_DURATION, 1);
                const value =
                    approximationReference.current! *
                    easeOut(t) *
                    LOADING_CEILING;
                currentReference.current = value;
                setDisplay(Math.round(value));
                frameReference.current = requestAnimationFrame(tick);
            } else {
                const t = Math.min(elapsed / SETTLE_DURATION, 1);
                const from = settleFromReference.current ?? 0;
                const value =
                    from + (finalValueReference.current! - from) * easeOut(t);
                setDisplay(Math.round(value));
                if (t < 1) frameReference.current = requestAnimationFrame(tick);
                else setDisplay(finalValueReference.current!);
            }
        };

        if (isLoading) {
            cancelAnimationFrame(frameReference.current!);
            phaseReference.current = 'loading';
            startTimeReference.current = undefined;
            frameReference.current = requestAnimationFrame(tick);
        } else {
            cancelAnimationFrame(frameReference.current!);
            settleFromReference.current = currentReference.current;
            phaseReference.current = 'settling';
            startTimeReference.current = undefined;
            frameReference.current = requestAnimationFrame(tick);
        }

        return () => cancelAnimationFrame(frameReference.current!);
    }, [isLoading]);

    return display;
}
